import { apiFetch } from '../api';

const DEFAULT_REPORTS = [
  {
    id: 'daily-morning',
    name: 'Daily Brief',
    schedule: 'Every day · 09:00 IST',
    recipients: [],
    days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
    time: '09:00',
    enabled: true,
  },
  {
    id: 'weekly-digest',
    name: 'Weekly Risk Digest',
    schedule: 'Every Monday · 9:00 AM IST',
    recipients: [],
    days: ['mon'],
    time: '09:00',
    enabled: true,
  },
];

export async function getEmailActivity() {
  const activity = await apiFetch('/email/activity?limit=50');
  return activity.map(normalizeEmailActivity);
}

export async function getEmailRecipients() {
  const saved = await apiFetch('/email/recipients');
  if (!Array.isArray(saved)) return [];
  return saved.map(email => String(email || '').trim().toLowerCase()).filter(Boolean);
}

export async function saveEmailRecipients(recipients) {
  const seen = new Set();
  const clean = (recipients || [])
    .map(email => String(email || '').trim().toLowerCase())
    .filter(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    .filter(email => {
      if (seen.has(email)) return false;
      seen.add(email);
      return true;
    });
  return apiFetch('/email/recipients', {
    method: 'PUT',
    body: JSON.stringify({ recipients: clean }),
  });
}

export async function getPrimaryEmailRecipient(fallback = '') {
  return (await getEmailRecipients())[0] || fallback;
}

export function normalizeEmailActivity(activity) {
  if (!activity) return activity;
  const recipients = activity.recipients || (activity.recipient ? [activity.recipient] : []);
  return {
    ...activity,
    createdAt: activity.createdAt || activity.created_at || new Date().toISOString(),
    recipient: activity.recipient || recipients.join(', '),
    recipients,
    recordTitle: activity.recordTitle || activity.record_title,
  };
}

export async function sendAlertBrief(record, recipient = '') {
  const subject = `[Corp Sentinel] ${record.company} — ${record.title || 'Alert brief'}`;
  const savedRecipients = recipient ? [recipient] : await getEmailRecipients();
  const activity = await apiFetch('/email/brief', {
    method: 'POST',
    body: JSON.stringify({
      record,
      recipients: savedRecipients,
      subject,
    }),
  });
  return normalizeEmailActivity(activity);
}

export async function sendExecutiveBriefEmail(brief, recipient = '', subject = '') {
  const recipients = recipient
    ? String(recipient || '')
    .split(',')
    .map(email => email.trim())
        .filter(Boolean)
    : await getEmailRecipients();
  const activity = await apiFetch('/email/executive-brief', {
    method: 'POST',
    body: JSON.stringify({
      brief,
      recipients,
      // The server writes the subject (what is inside) unless the caller gives one.
      ...(subject ? { subject } : {}),
    }),
  });
  return normalizeEmailActivity(activity);
}

export async function logMockAlertBrief(record, recipient = '') {
  const subject = `[Corp Sentinel] ${record.company} — ${record.title || 'Alert brief'}`;
  const recipients = recipient ? [recipient] : await getEmailRecipients();
  const activity = await apiFetch('/email/brief', {
    method: 'POST',
    body: JSON.stringify({ record, recipients, subject }),
  });
  return normalizeEmailActivity(activity);
}

export async function getScheduledReports() {
  const saved = await apiFetch('/email/scheduled-reports');
  if (!Array.isArray(saved) || !saved.length) return DEFAULT_REPORTS;
  // The daily brief is a real backend job, so its row must exist even in a database saved
  // before it was added. (daily-evening was retired: dropped here so saving removes it.)
  const kept = saved.filter(r => r.id !== 'daily-evening');
  const missing = DEFAULT_REPORTS.filter(d => d.id === 'daily-morning' && !kept.some(r => r.id === d.id));
  return missing.length ? [...kept, ...missing] : kept;
}

export async function saveScheduledReports(reports) {
  return apiFetch('/email/scheduled-reports', {
    method: 'PUT',
    body: JSON.stringify({ reports }),
  });
}

export async function toggleScheduledReport(reportId) {
  const reports = (await getScheduledReports()).map(r => (
    r.id === reportId ? { ...r, enabled: !r.enabled } : r
  ));
  return saveScheduledReports(reports);
}

export async function getRecentEmailCount(hours = 24) {
  const cutoff = Date.now() - hours * 60 * 60 * 1000;
  return (await getEmailActivity()).filter(a => new Date(a.createdAt).getTime() >= cutoff).length;
}

/** Server-built brief for the Briefs page: exactly what Send now will email. */
export function previewBrief({ window, include, unverified }) {
  return apiFetch('/email/executive-brief/preview', {
    method: 'POST',
    body: JSON.stringify({ window, include, unverified }),
  });
}

export function getBriefPrefs() {
  return apiFetch('/email/brief-prefs');
}

export function saveBriefPrefs(prefs) {
  return apiFetch('/email/brief-prefs', { method: 'PUT', body: JSON.stringify(prefs) });
}
